import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '../../config';
import axios from 'axios';
import makeStyles from '@mui/styles/makeStyles';

import Business from '@mui/icons-material/Business';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Leaderboard from '@mui/icons-material/Leaderboard';
import Accessibility from '@mui/icons-material/Accessibility';

import GridItem from '@/components/Grid/GridItem';
import GridContainer from '@/components/Grid/GridContainer';

import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardIcon from '@/components/Card/CardIcon';
import CardFooter from '@/components/Card/CardFooter';

import { CSVLink } from 'react-csv';
import DatePicker from 'react-datepicker';
import Button from '@/components/CustomButtons/Button';

import styles from '@/assets/jss/material-dashboard-react/views/dashboardStyle';
import search from '../../assets/img/search.svg';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';
import BarChart from './BarChart';
import DoughnutChart from './DoughnutChart';
import moment from 'moment';

const useStyles = makeStyles(styles);

export default function Dashboard() {
  const classes = useStyles();
  const { t } = useTranslation();

  const [awaitingRefunds, setAwaitingRefunds] = useState(0);
  const [startdate, setStartdate] = useState(
    moment().subtract(1, 'month').toDate(),
  );
  const [enddate, setEnddate] = useState(new Date());
  const [refundMethodsTransaction, setRefundMethodsTransaction] = useState([]);
  const [facilityID, setFacilityID] = useState(null);
  const { facilities } = useGetFacilities({
    allColumns: false,
  });
  const [radiovalue, setRadiovalue] = useState('transaction');

  const [pieChartData, setPieChartData] = useState({
    claimed: 0,
    unclaimed: 0,
    escheated: 0,
    requested: 0,
  });

  const changeDate = (event) => {
    setStartdate(event);
  };

  const changeEndDate = (event) => {
    setEnddate(event);
  };

  const handleFacility = (e) => {
    if (e.target.value === 'null') {
      setFacilityID(null);
    }
    setFacilityID(e.target.value);
  };

  const searchReport = () => {
    if (startdate > enddate) {
      alert(t('dashboard.admin.messages.dateValidation'));
    } else {
      axios
        .post(API_BASE_URL + '/search_refund_report', {
          start_date: startdate,
          end_date: enddate,
          facility_id: facilityID,
        })
        .then((res) => {
          setPieChartData(res.data.totals);
          setRefundMethodsTransaction(res.data.countAndTotalPerMethod);
          setAwaitingRefunds(res.data.awaitingRefunds);
        })
        .catch((err) => {
          console.log('request error ' + err.message);
        });
    }
  };

  const handleClicks = (event) => {
    setRadiovalue(event.target.value);
  };

  useEffect(() => {
    searchReport();
  }, []);

  const headers1 = [
    { label: t('dashboard.admin.csv.refundTypeHeader'), key: 'method' },
    { label: t('dashboard.admin.csv.transactionCountHeader'), key: 'count' },
    { label: t('dashboard.admin.csv.refundedAmountHeader'), key: 'amount' },
  ];
  const headers3 = [
    { label: t('dashboard.admin.csv.refundClaimedHeader'), key: 'claimed' },
    { label: t('dashboard.admin.csv.refundUnclaimedHeader'), key: 'unclaimed' },
    { label: t('dashboard.admin.csv.refundEscheatedHeader'), key: 'escheated' },
  ];

  const csvReport1 = {
    data: refundMethodsTransaction,
    headers: headers1,
    filename: 'Distribution_of_refund_methods.csv',
  };
  const csvReport3 = {
    data: [pieChartData],
    headers: headers3,
    filename: 'Refund_Claim_VS_Refund_UnClaim.csv',
  };

  return (
    <div>
      <div className='dash-tiles'>
        <div className='dash_card'>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <Card className='filterCard'>
                <div>
                  {' '}
                  <div className='t-header search-facility'>
                    <div className='t-header-left'>
                      {t('dashboard.admin.dateFilters.fromLabel')}&nbsp;&nbsp;
                      <DatePicker
                        className='full-width-elem-date-picker'
                        id='dt'
                        selected={startdate}
                        onChange={changeDate}
                      />
                    </div>
                    <div className='t-header-left'>
                      {t('dashboard.admin.dateFilters.toLabel')}&nbsp;&nbsp;
                      <DatePicker
                        className='full-width-elem-date-picker'
                        id='dt'
                        selected={enddate}
                        onChange={changeEndDate}
                      />
                    </div>
                    <div className='t-header-right'>
                      <select
                        className='full-width-elem'
                        id='status'
                        onChange={handleFacility}>
                        <option value={'null'}>
                          {t('dashboard.admin.filters.facilityPlaceholder')}
                        </option>
                        {facilities?.map((obj, index) => (
                          <option value={obj.id} key={index}>
                            {obj.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='search_t_head'>
                      <Button
                        type='button'
                        color='primary'
                        onClick={searchReport}>
                        <img src={search} className='search_ico' />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </GridItem>
          </GridContainer>
        </div>

        <GridContainer>
          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color='warning' stats icon>
                <CardIcon color='warning'>
                  <Business color='secondary' />
                </CardIcon>
                <p className={classes.cardCategory}>
                  {t('dashboard.admin.cards.totalFacilities')}
                </p>
                <h3 className={classes.cardTitle}>{facilities.length}</h3>
              </CardHeader>
              <CardFooter stats>
                <div className={classes.stats}>
                  <Link to='/admin/facilities'>
                    {t('dashboard.admin.stats.totalFacilities')}
                  </Link>
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color='success' stats icon>
                <CardIcon color='danger'>
                  <AttachMoney color='secondary' />
                </CardIcon>
                <p className={classes.cardCategory}>
                  {t('dashboard.admin.cards.amountRefunded')}
                </p>
                <h3 className={classes.cardTitle}>${pieChartData.claimed}</h3>
              </CardHeader>
              <CardFooter stats>
                <div className={classes.stats}>
                  {t('dashboard.admin.stats.totalAmountRefunded')}
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color='success' stats icon>
                <CardIcon color='success'>
                  <Leaderboard color='secondary' />
                </CardIcon>
                <p className={classes.cardCategory}>
                  {t('dashboard.admin.cards.unclaimedAmount')}
                </p>
                <h3 className={classes.cardTitle}>${pieChartData.unclaimed}</h3>
              </CardHeader>
              <CardFooter stats>
                <div className={classes.stats}>
                  {t('dashboard.admin.stats.amountOutstanding')}
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color='info' stats icon>
                <CardIcon color='info'>
                  <Accessibility color='secondary' />
                </CardIcon>
                <p className={classes.cardCategory}>
                  {t('dashboard.admin.cards.awaitingRefunds')}
                </p>
                <h3 className={classes.cardTitle}>{awaitingRefunds}</h3>
              </CardHeader>
              <CardFooter stats>
                <div className={classes.stats}>
                  {t('dashboard.admin.stats.totalAwaitingRefunds')}
                </div>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </div>

      <GridContainer>
        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('dashboard.admin.charts.distributionTitle')}
            </h3>
            <div style={{ width: '100%', height: '80%', paddingTop: '10%' }}>
              <BarChart
                showCount={radiovalue === 'transaction'}
                data={refundMethodsTransaction}
                role={'admin'}
                isCustomPaymentTypesEnabled={false}
              />
            </div>

            <div className='dashboardRadio'>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='transaction'
                  onChange={handleClicks}
                  checked={radiovalue === 'transaction'}
                  name='radiocheck'
                />{' '}
                {t('dashboard.admin.radio.numberOfTransactions')}
              </div>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='refund'
                  checked={radiovalue === 'refund'}
                  onChange={handleClicks}
                  name='radiocheck'
                />{' '}
                {t('dashboard.admin.radio.amountByRefund')}
              </div>
            </div>
            <CSVLink
              {...csvReport1}
              style={{ cursor: 'pointer' }}
              className='dashboardBtn'>
              {t('dashboard.admin.charts.downloadReport')}
            </CSVLink>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('dashboard.admin.charts.unclaimedVsRequestedVsClaimed')}
            </h3>
            <div
              style={{
                width: '100%',
                position: 'relative',
                height: '90%',
                paddingTop: '10%',
              }}>
              <DoughnutChart
                claimed={pieChartData.claimed}
                unclaimed={pieChartData.unclaimed}
                requested={pieChartData.requested}
              />
            </div>
            <CSVLink
              {...csvReport3}
              style={{ cursor: 'pointer' }}
              className='dashboardBtn'>
              {t('dashboard.admin.charts.downloadReport')}
            </CSVLink>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
